import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent, Dialog, DialogContent, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import { isNil, sortBy } from 'lodash'
import { toast } from 'react-toastify'
import { BasicDialogAction, BasicDialogTitle } from 'common/components/modal/DialogComponents'
import Autocomplete from 'common/components/form/Autocomplete'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import { CONTRIBUTOR_TYPES } from '../../constants/CustomerConstants'
import ContactFormDialog from './ContactFormDialog'

// Dialog d'association d'un contact EXISTANT à un client (choix contact + type).
// Bouton « Créer un contact » -> ContactFormDialog ; le nouveau contact revient dans l'autocomplete.
const AssociateContactDialog = ({
    contributor: contributorDefault,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const [contacts, setContacts] = useState([])
    const [idContact, setIdContact] = useState(contributorDefault?.idContact)
    const [typeContributor, setTypeContributor] = useState(contributorDefault?.typeContributor)
    const [createOpen, setCreateOpen] = useState(false)

    const isEdit = !isNil(contributorDefault?.idContact)

    const reloadContacts = useCallback(() => {
        aquaFetch(ApplicationConf.gdl.contacts())
            .then(res => setContacts(res ?? []))
            .catch(() => toast.error('Erreur lors du chargement des contacts'))
    }, [])

    useEffect(() => {
        reloadContacts()
    }, [reloadContacts])

    const contactItems = useMemo(
        () => sortBy(contacts.map(c => ({
            id: c.idContact,
            label: c.email ? `${c.name} — ${c.email}` : c.name,
            name: c.name,
        })), 'label'),
        [contacts],
    )

    const validate = () => {
        const contact = contacts.find(c => c.idContact === idContact)
        if (!contact) {
            return
        }
        onValidate({
            idContact,
            typeContributor,
            login: contact.login,
            name: contact.name,
            email: contact.email,
            telephone: contact.telephone,
            description: contact.description,
        })
        onClose()
    }

    return (
        <Dialog open maxWidth='md' fullWidth>
            <BasicDialogTitle onClose={onClose}>
                {isEdit ? 'Modifier l\'intervenant' : 'Associer un contact'}
            </BasicDialogTitle>
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container columnSpacing={2} rowSpacing={1} alignItems='center' style={{ paddingTop: '5px' }}>
                            <Grid size={8}>
                                <Autocomplete
                                    label='Contact'
                                    items={contactItems}
                                    value={idContact}
                                    onChange={setIdContact}
                                    required
                                />
                            </Grid>
                            <Grid container size={4} justifyContent='flex-end'>
                                <Button variant='outlined' onClick={() => setCreateOpen(true)}>
                                    Créer un contact
                                </Button>
                            </Grid>
                            <Grid size={12}>
                                <Autocomplete
                                    label='Type de contact'
                                    items={CONTRIBUTOR_TYPES}
                                    value={typeContributor}
                                    onChange={setTypeContributor}
                                    required
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <BasicDialogAction
                buttonLabel={isEdit ? 'Modifier' : 'Associer'}
                onValidate={validate}
                isDisabled={isNil(idContact) || !typeContributor}
            />
            {createOpen && (
                <ContactFormDialog
                    onClose={() => setCreateOpen(false)}
                    onSaved={(saved) => {
                        reloadContacts()
                        setIdContact(saved.idContact)
                    }}
                />
            )}
        </Dialog>
    )
}

AssociateContactDialog.propTypes = {
    contributor: PropTypes.shape({
        idContact: PropTypes.number,
        typeContributor: PropTypes.string,
    }),
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export default AssociateContactDialog
